import { useLazyQuery, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import * as R from 'ramda';
import React, { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { down, up } from 'styled-breakpoints';
import styled from 'styled-components';
import * as yup from 'yup';

import Button from '@rebound/design-system/components/Button';
import { InputField } from '@rebound/design-system/components/FormHelpers';
import { Order } from '@rebound/shared-models/order/order.model';
import {
  ThemeColorScheme,
  UserJourneyEnum,
} from '@rebound/shared-models/retailer/retailer.model';

import {
  AuthGuestViaOrderLookupMutation,
  AuthGuestViaOrderLookupMutationResult,
} from '@modules/auth/auth.queries';
import { clearGuestSession, isGuestToken } from '@modules/auth/auth.session';
import { useRetailerData } from '@modules/retailer/retailer.context';

import {
  ListOrdersQuery,
  ListOrdersQueryResult,
  OrderLookupQuery,
  OrderLookupQueryResult,
} from '../dashboard.queries';

type OrderLookupFormValues = {
  orderNumber: string;
  contactDetails: string;
};

type OrderLookupFormProps = {
  setFoundOrder: React.Dispatch<React.SetStateAction<Order | null>>;
  orderReference?: string | undefined;
  emailOrPostalCode?: string | undefined;
  autoSearch?: boolean;
};

const OrderLookupForm = ({
  setFoundOrder,
  orderReference = '',
  emailOrPostalCode = '',
  autoSearch = false,
}: OrderLookupFormProps) => {
  const { t } = useTranslation();

  const OrderLookupFormValidationSchema = yup.object().shape({
    orderNumber: yup.string().required(t('errors.lookupOrderNumberValidation')),
    contactDetails: yup
      .string()
      .required(t('errors.lookupContactDetailsValidationWithoutPhone')),
  });

  const [authGuestViaOrderLookup] =
    useMutation<AuthGuestViaOrderLookupMutationResult>(
      AuthGuestViaOrderLookupMutation,
    );
  const [
    lookupOrder,
    { loading: orderLookupLoading, client, error: lookupError },
  ] = useLazyQuery<OrderLookupQueryResult>(OrderLookupQuery, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const ordersData = client?.readQuery({
        query: ListOrdersQuery,
      }) as ListOrdersQueryResult | null;

      if (!ordersData) {
        return data;
      }

      const existingOrder = ordersData.listOrders.find(
        (order) => order.id === data.orderLookup.id,
      );

      if (existingOrder) {
        return data;
      }

      const enrichedOrderList = [data.orderLookup, ...ordersData.listOrders];

      const sortedOrderList = R.reverse(
        R.sortBy(R.prop('orderDate'))(enrichedOrderList),
      );

      client?.writeQuery({
        query: ListOrdersQuery,
        data: { listOrders: sortedOrderList },
      });

      return data;
    },
  });

  const retailerData = useRetailerData();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
  } = useForm<OrderLookupFormValues>({
    resolver: yupResolver(OrderLookupFormValidationSchema),
    defaultValues: {
      orderNumber: orderReference,
      contactDetails: emailOrPostalCode,
    },
  });

  const retailer = retailerData.retailer;

  const formHandler = useCallback(
    (values: OrderLookupFormValues) => {
      const isOrderLookupJourney =
        retailer?.userJourneyRadio === UserJourneyEnum.ORDER_LOOKUP;

      // A code-login (non-guest) session already authorizes the journey and
      // must survive an order lookup; only guest sessions are replaced.
      const hasCodeLoginSession =
        Boolean(localStorage.getItem('token')) &&
        !isGuestToken(localStorage.getItem('token'));

      if (isOrderLookupJourney && !hasCodeLoginSession) {
        // A new lookup starts a new guest session; never reuse a previous
        // (possibly expired) guest token.
        clearGuestSession();
      }

      lookupOrder({
        variables: {
          orderNumber: values.orderNumber,
          contactDetails: values.contactDetails,
        },
      }).then((orderResult) => {
        if (isOrderLookupJourney && !hasCodeLoginSession) {
          if (!orderResult.data?.orderLookup) {
            setFoundOrder(null);
            return;
          }

          // The server re-verifies the order number and contact details and
          // returns a short-lived guest token for this order's consumer.
          authGuestViaOrderLookup({
            variables: {
              orderNumber: values.orderNumber,
              contactDetails: values.contactDetails,
            },
          })
            .then(({ data }) => {
              if (!data?.authGuestViaOrderLookup) {
                throw new Error('Guest authentication failed');
              }
              localStorage.setItem('token', data.authGuestViaOrderLookup.token);
              localStorage.setItem(
                'user',
                JSON.stringify(data.authGuestViaOrderLookup.user),
              );
              setFoundOrder(orderResult.data?.orderLookup ?? null);
            })
            .catch(() => {
              toast.error(t('errors.orderNotFoundError'));
              setFoundOrder(null);
            });
        } else {
          setFoundOrder(
            orderResult.data?.orderLookup
              ? orderResult.data?.orderLookup
              : null,
          );
        }
      });

      (window as any).heap.track('Lookup order', {
        orderNumber: values.orderNumber,
        query: values.contactDetails,
      });
    },
    [
      lookupOrder,
      setFoundOrder,
      retailer?.userJourneyRadio,
      authGuestViaOrderLookup,
      t,
    ],
  );

  useEffect(() => {
    if (autoSearch) {
      setValue('orderNumber', orderReference ?? '');
      setValue('contactDetails', emailOrPostalCode ?? '');
      trigger().then(() => {
        handleSubmit(formHandler)();
        localStorage.removeItem('orderReference');
        localStorage.removeItem('postalCode');
      });
    }
  }, [
    autoSearch,
    orderReference,
    emailOrPostalCode,
    setValue,
    trigger,
    handleSubmit,
    formHandler,
  ]);

  return (
    <Form onSubmit={handleSubmit(formHandler)}>
      <Controller
        name="orderNumber"
        control={control}
        render={({ field }) => (
          <InputFieldItem
            name={field.name}
            label={t('orderLookup.orderNumberLabel')}
            inputProps={{
              ...field,
              'data-cy': 'lookup-order-number-input',
            }}
            error={
              errors.orderNumber
                ? t('errors.lookupOrderNumberValidation')
                : lookupError?.graphQLErrors[0].extensions?.code ===
                    'INVALID_RETAILER'
                  ? t('errors.invalidRetailerError')
                  : lookupError?.graphQLErrors[0].extensions?.code ===
                      'USER_NOT_FOUND'
                    ? t('errors.userNotFoundError')
                    : lookupError?.graphQLErrors[0].extensions?.code ===
                        'ORDER_NOT_FOUND'
                      ? t('errors.orderNotFoundError')
                      : null
            }
          />
        )}
      />
      <Controller
        name="contactDetails"
        control={control}
        render={({ field }) => (
          <InputFieldItem
            name={field.name}
            label={t('orderLookup.contactDetailsLabelWithoutPhone')}
            inputProps={{
              ...field,
              'data-cy': 'lookup-contact-information-input',
            }}
            error={
              errors.contactDetails
                ? t('errors.lookupContactDetailsValidationWithoutPhone')
                : null
            }
          />
        )}
      />
      <SearchButton
        title={t('orderLookup.searchButton')}
        scheme={retailer?.theme.colorScheme ?? ThemeColorScheme.Indigo}
        data-cy="lookup-submit-button"
        loading={orderLookupLoading}
      />
    </Form>
  );
};

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const InputFieldItem = styled(InputField)`
  width: 100%;
  max-width: 370px;
  margin-bottom: 12px;

  ${down('sm')} {
    max-width: 100%;
  }
`;

const SearchButton = styled(Button)`
  width: 100%;
  max-width: 370px;

  ${down('sm')} {
    max-width: 100%;
  }

  ${up('lg')} {
    margin-top: 4px;
  }
`;

export default OrderLookupForm;
